import { useCallback, useMemo, useState } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { FilteredInteriorPackages } from '~/app/compositions/sidebar/interior-packages/components/filtered-interior-packages';
import { OptionsHeader } from '~/app/compositions/sidebar/options-header';
import { i18nKeys, useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { useSelectedEquipment } from '~/app/pages/summary/hooks/use-selected-equipment';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { hasTeaserText } from '~/components/selection-card-new/selection-card';
import { useActiveCard } from '~/components/slider-panel';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useAccessoriesQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useFinancing } from '~/graphql/hooks/use-financing';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';
import { CatalogItem, PriceOrError } from '~/graphql/mapper';
import { FeatureOrPackage, InteriorSeats, SelectionCardItem } from '~/graphql/types';
import GetUrlFromImageSet, {
	GetMediaArrayFromImageSet,
	GetMediaFromImageSet,
	GetModifiedItemsWithItemNameFromMedia,
	GetModifiedItemWithNameFromMedia,
} from '~/graphql/utils/get-from-image-set';

export default function InteriorPackages() {
	const { i18n } = useI18n();
	const { partRateByPr } = useFinancing();
	const { config } = useMarketConfig();
	const { isLoading, loadingFlags, isItemLoading, isItemChecked, carIdentifier } = useConfigurator();
	const { accessories } = useAccessoriesQuery(carIdentifier);
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { selectCard } = useActiveCard();
	const { feature } = useVATConfig();
	const { getName } = useDebugMode();
	const {
		configuration: { featuresAndPackages },
	} = useSelectedEquipment();
	const [seats] = [':Interior_Tiles'].map((id) => featuresAndPackages.find((features) => features.id.includes(id)));
	const [selectedTab, setSelectedTab] = useState('standard');
	const { mediaByProfileId } = useImages();

	const seatsTranslationsMapper = useMemo(
		() => ({
			sport: 'ngc.equipment.interiorPackages.tile2',
			standard: 'ngc.equipment.interiorPackages.tile1',
		}),
		[],
	);

	const availableInteriorPackages = useMemo(() => {
		if (!accessories || !accessories.configuredCar.accessoriesExclusive) {
			return [];
		}
		return accessories.configuredCar.accessoriesExclusive.packagesDetails.data?.filter(
			({ group }) => group.id.toUpperCase() === 'Interior_lines'.toUpperCase(),
		);
	}, [JSON.stringify(accessories)]);

	const { step, total } = useSteps('interiorPackages');

	const getSeatType = useCallback((familyId: string): 'sport' | 'standard' => {
		const [, extractedFamilyName] = familyId.split('.');

		switch (extractedFamilyName as InteriorSeats) {
			case InteriorSeats.SPORT:
				return 'sport';
			case InteriorSeats.STANDARD:
				return 'standard';
			default:
				return 'standard';
		}
	}, []);

	const trackSelectionClickEvent = useCallback(
		(
			label: string,
			item: FeatureOrPackage,
			elementName: string,
			value: string,
			selected?: boolean,
			eventAction = EventAction.InternalLink,
		) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction,
				eventName: 'click to change configuration',
				label,
				elementName,
				value,
				category: 'interiorPackages',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[isItemChecked, selectedTab, step, total],
	);

	const { items, filters } = useMemo(() => {
		if (!accessories || !accessories.configuredCar.accessoriesExclusive) {
			return { items: [], filters: [] };
		}

		// Get all available seat types (standard, sport)
		const seatTypes = availableInteriorPackages
			?.reduce((acc: { value: string; label: string }[], item) => {
				const seatType = getSeatType(item.family.id);
				const newItem = {
					value: seatType,
					label: i18n(seatsTranslationsMapper[seatType] as keyof typeof i18nKeys),
				};

				const exists = acc.some((existingItem) => existingItem.value === newItem.value);

				if (!exists) {
					acc.push(newItem);
				}

				return acc;
			}, [])
			.sort((seatType) => {
				if (seatType.value === 'standard') return -1; // bring standard seats to the front
				return 0;
			});

		const mappedItems = availableInteriorPackages?.map(
			(packageItem, index): SelectionCardItem => ({
				name: getName(packageItem) ?? packageItem.name,
				description: hasTeaserText(packageItem.texts?.teaserText) ? packageItem.texts?.teaserText : null,
				key: packageItem.pr7,
				loading: isItemLoading({ ...packageItem, featureId: packageItem.pr7, type: 'interiorPackage' }),
				selected: isItemChecked({ ...packageItem, featureId: packageItem.pr7, type: 'interiorPackage' }),
				readonly: loadingFlags.checkingConflicts || !packageItem.status.available,
				familyName: getSeatType(packageItem.family.id),
				subFamilyName: packageItem.group.id,
				imgUrl: GetUrlFromImageSet(packageItem.imageSets, 'small_hd_image') || '',
				galleryMedia: GetModifiedItemsWithItemNameFromMedia(
					GetMediaArrayFromImageSet(packageItem.imageSets, 'teaser_image') || [],
					packageItem.name || '',
				),
				onSelect: () =>
					selectCard(
						index,
						{
							...packageItem,
							featureId: packageItem.pr7,
							type: 'interiorPackage',
							totalPrice: packageItem.price,
							monthlyRate: partRateByPr(packageItem.pr7),
						} as CatalogItem,
						() =>
							trackSelectionClickEvent(
								packageItem.name || '',
								packageItem,
								'card',
								`selection feature app: interiorPackages: ${selectedTab}`,
								isItemChecked({ ...packageItem, featureId: packageItem.pr7, type: 'interiorPackage' }),
							),
					),
				standard: packageItem.status.standard,
				price: config.showPrices
					? {
							totalPrice: packageItem.price as PriceOrError,
							monthlyRate: !config.hideMonthlyRates ? partRateByPr(packageItem.pr7) : undefined,
							feature,
						}
					: undefined,

				textLink: {
					label: i18n('ngc.actions.moreInfo'),
					onTextLinkClick: () =>
						openMoreInformationDialog(
							{
								featureId: packageItem.pr7,
								type: 'interiorPackage',
								name: packageItem.name || '',
								status: packageItem.status,
							},
							packageItem,
							() =>
								trackSelectionClickEvent(
									packageItem.name || '',
									packageItem,
									'button',
									'details layer',
									isItemChecked({
										...packageItem,
										featureId: packageItem.pr7,
										type: 'interiorPackage',
									}),
								),
							step,
							total,
							'interiorPackages',
						),
				},
				onDescriptionToggle: (showDescription: boolean) =>
					trackSelectionClickEvent(
						showDescription ? i18n('ngc.actions.hideDetails') : i18n('ngc.actions.showDetails'),
						packageItem,
						'text link',
						'details accordion',
						undefined,
						EventAction.Content,
					),
				disclaimerHeadline: packageItem.texts?.disclaimerHeadline || '',
				disclaimerContent: packageItem.texts?.disclaimerText || '',
				disclaimerStartDate: packageItem.texts?.disclaimerStartDate || '',
				disclaimerEndDate: packageItem.texts?.disclaimerEndDate || '',
			}),
		);

		return { items: mappedItems || [], filters: seatTypes || [] };
	}, [
		JSON.stringify(accessories),
		i18n,
		getName,
		isItemLoading,
		isItemChecked,
		trackSelectionClickEvent,
		loadingFlags.checkingConflicts,
		config.showPrices,
		config.hideMonthlyRates,
		JSON.stringify(feature),
		selectCard,
		openMoreInformationDialog,
		step,
		seatsTranslationsMapper,
		total,
		getSeatType,
	]);

	const interiorPackageSelected = items.find((a) => a.selected);

	const selectedItemMedia = GetModifiedItemWithNameFromMedia(
		GetMediaFromImageSet(seats?.families?.[0]?.items?.[0]?.imageSets, 'interior_tile_hd_square'),
		seats?.families?.[0]?.items?.[0].name || '',
	);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'interiorPackages',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const onTabClick = useCallback(
		(tab: string) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.Content,
				eventName: `selections - tab navi - click on ${tab}`,
				label: filters.find((f) => f.value === tab)?.label ?? '',
				category: 'interiorPackages',
				elementName: 'tab',
				elementCurrentStep: step,
				elementTotalSteps: total,
			});
			setSelectedTab(tab);
		},
		[trackSelectionClickEvent],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === 'interiorPackages')?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			hide={items.length === 0}
			id={'interior-packages'}
			galleryType={'singleView'}
			media={
				interiorPackageSelected
					? [
							[mediaByProfileId[ImageViewsEnum.INTERIOR_SEATS_SEATS], selectedItemMedia!],
							[mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD]],
							[mediaByProfileId[ImageViewsEnum.INTERIOR_REAR_REARSEATS]],
						]
					: [[mediaByProfileId[ImageViewsEnum.INTERIOR_SEATS_SEATS], selectedItemMedia!]]
			}
			headline={
				<OptionsHeader
					id={'interior-packages'}
					title={i18n('ngc.equipment.interiorPackages')}
					step={step}
					total={total}
				/>
			}
			disclaimer={categoryDisclaimer}>
			<FilteredInteriorPackages
				items={items}
				tabs={filters}
				selectedTab={selectedTab}
				setSelectedTab={onTabClick}
			/>
		</ConfigurationStep>
	);
}
